import React, { useState, useEffect, useCallback, useRef } from "react";
import { apiClient } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { ScrollArea } from "./ui/scroll-area";
import { Switch } from "./ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { 
  Scan, 
  Plus, 
  Trash2, 
  Play, 
  X,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Volume,
  Activity
} from "lucide-react";
import { toast } from "sonner";

// Format number
const formatNumber = (num, decimals = 2) => {
  if (num === undefined || num === null) return "-";
  return Number(num).toLocaleString('en-IN', { 
    minimumFractionDigits: decimals, 
    maximumFractionDigits: decimals 
  });
};

// Format volume
const formatVolume = (num) => {
  if (num === undefined || num === null) return "-";
  if (num >= 10000000) return (num / 10000000).toFixed(2) + " Cr";
  if (num >= 100000) return (num / 100000).toFixed(2) + " L";
  if (num >= 1000) return (num / 1000).toFixed(2) + " K";
  return num.toLocaleString('en-IN');
};

// Condition types
const CONDITION_TYPES = [
  { value: "volume_spike", label: "Volume Spike", icon: Volume, description: "Volume >= threshold" },
  { value: "ltq_spike", label: "LTQ Spike", icon: Activity, description: "Last Traded Qty >= threshold" },
  { value: "aggressive_buy", label: "Aggressive Buy", icon: TrendingUp, description: "Agg Buy Qty >= threshold" },
  { value: "aggressive_sell", label: "Aggressive Sell", icon: TrendingDown, description: "Agg Sell Qty >= threshold" },
  { value: "bid_ask_imbalance", label: "Bid/Ask Imbalance", icon: AlertTriangle, description: "Imbalance % >= threshold" },
  { value: "oi_change", label: "OI Change", icon: Activity, description: "|OI Change| >= threshold" },
  { value: "price_breakout", label: "Price Breakout", icon: TrendingUp, description: "LTP >= High * (1 + threshold%)" },
  { value: "vwap_deviation", label: "VWAP Deviation", icon: AlertTriangle, description: "|LTP - VWAP| / VWAP >= threshold%" },
];

// Scanner Condition Item
const ConditionItem = ({ condition, onDelete, onToggle }) => {
  const condType = CONDITION_TYPES.find(t => t.value === condition.condition_type);
  const Icon = condType?.icon || Activity;

  return (
    <div className="flex items-center justify-between p-3 bg-[#0A0A0A] rounded-md border border-[#27272A]">
      <div className="flex items-center gap-3">
        <div className={`w-8 h-8 rounded flex items-center justify-center ${condition.enabled ? 'bg-blue-600/20' : 'bg-[#27272A]'}`}>
          <Icon className={`w-4 h-4 ${condition.enabled ? 'text-blue-400' : 'text-[#52525B]'}`} />
        </div>
        <div>
          <p className="text-sm font-medium text-white">{condition.name}</p>
          <p className="text-xs text-[#71717A]">
            {condType?.label || condition.condition_type} @ {formatNumber(condition.threshold)}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Switch
          checked={condition.enabled}
          onCheckedChange={() => onToggle(condition.id)}
          className="data-[state=checked]:bg-blue-600"
        />
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDelete(condition.id)}
          className="h-8 w-8 p-0 hover:text-red-500"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

// Scanner Match Result
const MatchResult = ({ match, onView }) => {
  const isAggBuy = match.tick?.agg_buy > 0;
  const isAggSell = match.tick?.agg_sell > 0;

  return (
    <div className={`p-3 rounded-md border ${
      isAggBuy ? 'bg-green-900/20 border-green-800/30' : 
      isAggSell ? 'bg-red-900/20 border-red-800/30' : 
      'bg-[#0A0A0A] border-[#27272A]'
    }`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-white">{match.symbol}</p>
          <p className="text-xs text-[#71717A]">{match.condition_name}</p>
        </div>
        <div className="text-right">
          <p className={`text-sm font-mono ${
            isAggBuy ? 'text-green-400' : isAggSell ? 'text-red-400' : 'text-white'
          }`}>
            {formatNumber(match.tick?.last_price)}
          </p>
          <p className="text-xs text-[#52525B]">
            {new Date(match.timestamp).toLocaleTimeString()}
          </p>
        </div>
      </div>
    </div>
  );
};

export default function ScannerPanel({ instruments, subscribedTokens, tickData, onClose }) {
  const [conditions, setConditions] = useState([]);
  const [scanResults, setScanResults] = useState([]);
  const [isScanning, setIsScanning] = useState(false);
  const [autoScan, setAutoScan] = useState(false);
  const [newCondition, setNewCondition] = useState({
    name: "",
    condition_type: "volume_spike",
    threshold: 100000
  });

  // Fetch existing conditions
  const fetchConditions = useCallback(async () => {
    try {
      const response = await apiClient.get("/scanner/conditions");
      setConditions(response.data.conditions || []);
    } catch (error) {
      console.error("Failed to fetch conditions:", error);
    }
  }, []);

  // Create new condition
  const createCondition = async () => {
    if (!newCondition.name || !newCondition.threshold) {
      toast.error("Please fill all fields");
      return;
    }

    try {
      const response = await apiClient.post("/scanner/conditions", {
        name: newCondition.name,
        condition_type: newCondition.condition_type,
        threshold: newCondition.threshold,
        enabled: true
      });
      
      setConditions(prev => [...prev, response.data.condition]);
      setNewCondition({ name: "", condition_type: "volume_spike", threshold: 100000 });
      toast.success("Scanner condition created");
    } catch (error) {
      toast.error("Failed to create condition");
    }
  };

  // Delete condition
  const deleteCondition = async (conditionId) => {
    try {
      await apiClient.delete(`/scanner/conditions/${conditionId}`);
      setConditions(prev => prev.filter(c => c.id !== conditionId));
      toast.success("Condition deleted");
    } catch (error) {
      toast.error("Failed to delete condition");
    }
  };

  // Toggle condition
  const toggleCondition = (conditionId) => {
    setConditions(prev => prev.map(c => 
      c.id === conditionId ? { ...c, enabled: !c.enabled } : c
    ));
  };

  // Run scanner
  const runScanner = async () => {
    setIsScanning(true);
    try {
      const response = await apiClient.post("/scanner/scan");
      setScanResults(response.data.matches || []);
      if (response.data.count > 0) {
        toast.success(`Found ${response.data.count} matches`);
      }
    } catch (error) {
      toast.error("Scanner failed");
    } finally {
      setIsScanning(false);
    }
  };

  // Auto-scan effect with high-frequency polling (50ms) using requestAnimationFrame
  const rafIdRef = useRef(null);
  const lastScanTimeRef = useRef(0);
  
  useEffect(() => {
    if (!autoScan) {
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }
      return;
    }
    
    let isMounted = true;
    const SCAN_INTERVAL = 50; // 50ms scanning
    
    const autoScanLoop = async () => {
      if (!isMounted) return;
      
      const now = Date.now();
      if (now - lastScanTimeRef.current >= SCAN_INTERVAL) {
        lastScanTimeRef.current = now;
        
        // Run scan in non-blocking way
        try {
          const response = await apiClient.post("/scanner/scan");
          if (isMounted && response.data.matches?.length > 0) {
            setScanResults(prev => {
              // Only update if there are new matches
              const newMatches = response.data.matches;
              if (newMatches.length !== prev.length || 
                  newMatches[0]?.timestamp !== prev[0]?.timestamp) {
                return newMatches;
              }
              return prev;
            });
          }
        } catch (e) {
          // Silently fail for auto-scan
        }
      }
      
      if (isMounted) {
        rafIdRef.current = requestAnimationFrame(autoScanLoop);
      }
    };
    
    rafIdRef.current = requestAnimationFrame(autoScanLoop);
    
    return () => {
      isMounted = false;
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, [autoScan]);

  // Initial load
  useEffect(() => {
    fetchConditions();
  }, [fetchConditions]);

  return (
    <div className="w-96 border-l border-[#27272A] bg-[#111111] flex flex-col flex-shrink-0" data-testid="scanner-panel">
      {/* Header */}
      <div className="p-4 border-b border-[#27272A] flex items-center justify-between">
        <h2 className="font-semibold text-white flex items-center gap-2">
          <Scan className="w-4 h-4 text-purple-400" />
          Advanced Scanner
        </h2>
        <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0">
          <X className="w-4 h-4" />
        </Button>
      </div>

      {/* Create New Condition */}
      <div className="p-4 border-b border-[#27272A] space-y-3">
        <h3 className="text-sm font-medium text-[#A1A1AA]">Create Scanner Condition</h3>
        
        <Input
          value={newCondition.name}
          onChange={(e) => setNewCondition(prev => ({ ...prev, name: e.target.value }))}
          placeholder="Condition name..."
          className="bg-[#0A0A0A] border-[#27272A] text-white"
        />

        <Select
          value={newCondition.condition_type}
          onValueChange={(val) => setNewCondition(prev => ({ ...prev, condition_type: val }))}
        >
          <SelectTrigger className="bg-[#0A0A0A] border-[#27272A] text-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CONDITION_TYPES.map(type => (
              <SelectItem key={type.value} value={type.value}>
                <div className="flex items-center gap-2">
                  <type.icon className="w-4 h-4" />
                  <span>{type.label}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="text-xs text-[#52525B]">
          {CONDITION_TYPES.find(t => t.value === newCondition.condition_type)?.description}
        </div>

        <Input
          type="number"
          value={newCondition.threshold}
          onChange={(e) => setNewCondition(prev => ({ ...prev, threshold: parseFloat(e.target.value) }))}
          placeholder="Threshold value"
          className="bg-[#0A0A0A] border-[#27272A] text-white"
        />

        <Button onClick={createCondition} className="w-full bg-purple-600 hover:bg-purple-700">
          <Plus className="w-4 h-4 mr-2" />
          Add Condition
        </Button>
      </div>

      {/* Conditions List */}
      <div className="p-4 border-b border-[#27272A]">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-[#A1A1AA]">Active Conditions ({conditions.length})</h3>
        </div>
        
        <ScrollArea className="max-h-48">
          <div className="space-y-2">
            {conditions.length === 0 ? (
              <p className="text-[#52525B] text-sm text-center py-4">No conditions configured</p>
            ) : (
              conditions.map(condition => (
                <ConditionItem
                  key={condition.id}
                  condition={condition}
                  onDelete={deleteCondition}
                  onToggle={toggleCondition}
                />
              ))
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Scanner Controls */}
      <div className="p-4 border-b border-[#27272A]">
        <div className="flex items-center gap-2">
          <Button 
            onClick={runScanner} 
            disabled={isScanning || conditions.filter(c => c.enabled).length === 0}
            className="flex-1 bg-blue-600 hover:bg-blue-700"
          >
            <Play className="w-4 h-4 mr-2" />
            {isScanning ? "Scanning..." : "Run Scanner"}
          </Button>
          
          <div className="flex items-center gap-2 px-3 py-2 bg-[#0A0A0A] rounded border border-[#27272A]">
            <span className="text-xs text-[#71717A]">Auto</span>
            <Switch
              checked={autoScan}
              onCheckedChange={setAutoScan}
              className="data-[state=checked]:bg-green-600"
            />
          </div>
        </div>
        
        {autoScan && (
          <div className="mt-2 flex items-center gap-2">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            <span className="text-xs text-green-400">Auto-scanning every 2 seconds</span>
          </div>
        )}
      </div>

      {/* Scan Results */}
      <div className="flex-1 overflow-hidden">
        <div className="p-4 border-b border-[#27272A] sticky top-0 bg-[#111111]">
          <h3 className="text-sm font-medium text-[#A1A1AA] flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-yellow-500" />
            Matches ({scanResults.length})
          </h3>
        </div>
        
        <ScrollArea className="h-[calc(100%-50px)]">
          <div className="p-4 space-y-2">
            {scanResults.length === 0 ? (
              <p className="text-[#52525B] text-sm text-center py-4">
                {conditions.filter(c => c.enabled).length === 0 
                  ? "Enable at least one condition to scan"
                  : "No matches found. Run scanner to check."}
              </p>
            ) : (
              scanResults.map((match, index) => (
                <MatchResult key={`${match.token}-${index}`} match={match} />
              ))
            )}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
}
